import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import useContainerWidth from '@src/hooks/useContainerWidth';
import usePdfScroll from '@src/hooks/usePdfScroll';
import usePdfZoom from '@src/hooks/usePdfZoom';

// react-pdf ships an ESM pdfjs worker which cannot run during the Gatsby
// Node build. We lazy-require it on the client only.
let pdfjs;
let Document;
let Page;
if (typeof window !== 'undefined') {
  // eslint-disable-next-line global-require
  const reactPdf = require('react-pdf');
  Document = reactPdf.Document;
  Page = reactPdf.Page;
  pdfjs = reactPdf.pdfjs;
  try {
    // Pin the pdf.js worker to the matching CDN build so we never ship the
    // worker bundle ourselves.
    pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
  } catch (err) {
    // worker load failure — render handler below will fall back to iframe
  }
}

// PdfViewer renders the PDF in a scrollable container and reports scroll
// position + current page to the parent.
//
// Sizing strategy: each <Page> is sized via the `width` prop (not `scale`)
// using the container's measured width, multiplied by the user's zoom
// factor. This preserves each page's native aspect ratio — no stretching
// on narrow viewports — and re-computes on window resize.
//
// Imperative API (via ref):
//   jumpToPage(pageNumber) — scrolls the given 1-indexed page into view.
const PdfViewer = forwardRef(function PdfViewer(
  { url, onCurrentPageChange, onScrollPctChange, onZoom, onDocumentLoaded },
  ref,
) {
  const containerRef = useRef(null);
  const pageRefs = useRef(new Map());
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [isMounted, setIsMounted] = useState(false);

  // Defer any react-pdf rendering until after client mount.
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const canRenderPdfJs = useMemo(
    () => typeof window !== 'undefined' && Document && Page && isMounted,
    [isMounted],
  );

  // Container width measurement (matches px-4 padding = 32px)
  const containerWidth = useContainerWidth(containerRef, {
    padding: 32,
    minWidth: 320,
    enabled: isMounted,
  });

  // Scroll tracking — reports current page and scroll percentage
  usePdfScroll({
    containerRef,
    pageRefs,
    numPages,
    onCurrentPageChange,
    onScrollPctChange,
  });

  // Zoom state and handlers
  const { zoom, zoomIn, zoomOut } = usePdfZoom({ onZoom });

  useImperativeHandle(
    ref,
    () => ({
      jumpToPage: (pageNumber) => {
        const el = pageRefs.current.get(pageNumber);
        const container = containerRef.current;
        if (!el || !container) return;
        const top = el.offsetTop - container.offsetTop - 12;
        container.scrollTo({ top, behavior: 'smooth' });
      },
    }),
    [],
  );

  const shouldFallbackToIframe = !canRenderPdfJs || loadError;

  if (shouldFallbackToIframe) {
    return (
      <div className="relative h-full w-full bg-v5-neutral-100">
        <iframe
          title="Program brochure"
          src={url}
          className="h-full w-full border-0"
        />
      </div>
    );
  }

  // Effective page width: measured container width * user zoom factor.
  const renderWidth = containerWidth
    ? Math.round(containerWidth * zoom)
    : undefined;

  return (
    <div className="relative h-full w-full">
      {/* Zoom control rail */}
      <div className="absolute right-4 top-4 z-10 flex flex-col gap-2">
        <button
          type="button"
          onClick={zoomIn}
          aria-label="Zoom in"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-xl leading-none text-v5-neutral-600 shadow hover:bg-v5-green-100"
        >
          +
        </button>
        <button
          type="button"
          onClick={zoomOut}
          aria-label="Zoom out"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-xl leading-none text-v5-neutral-600 shadow hover:bg-v5-green-100"
        >
          −
        </button>
      </div>

      <div
        ref={containerRef}
        className="h-full w-full overflow-y-auto overflow-x-auto bg-v5-green-700 px-4 py-6"
      >
        <div className="mx-auto flex flex-col items-center gap-6">
          <Document
            file={url}
            onLoadSuccess={({ numPages: n }) => {
              setNumPages(n);
              onDocumentLoaded && onDocumentLoaded({ numPages: n });
            }}
            onLoadError={(err) => {
              // Most common cause: S3 bucket missing CORS headers.
              // eslint-disable-next-line no-console
              console.warn(
                '[BrochureView] PDF load failed, falling back to iframe',
                err,
              );
              setLoadError(err);
            }}
            loading={
              <div className="py-20 text-center text-white/80">
                Loading brochure…
              </div>
            }
          >
            {Array.from({ length: numPages }, (_, idx) => {
              const pageNumber = idx + 1;
              return (
                <div
                  key={`page_${pageNumber}`}
                  ref={(el) => {
                    if (el) pageRefs.current.set(pageNumber, el);
                  }}
                  className="overflow-hidden rounded-xl bg-white shadow-lg"
                >
                  <Page
                    pageNumber={pageNumber}
                    width={renderWidth}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                </div>
              );
            })}
          </Document>
        </div>
      </div>
    </div>
  );
});

export default PdfViewer;
